import ChevronLeftSharpIcon from "@mui/icons-material/ChevronLeftSharp";
import ChevronRightSharpIcon from "@mui/icons-material/ChevronRightSharp";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { FullScreenLoader } from "components/loader/FullScreenLoader";
import type { DigitalSample } from "hooks/digital-samples";
import type { Dispatch, FC } from "react";
import Lightbox from "react-spring-lightbox";

export interface DigitalSampleImage {
  digitalSample: DigitalSample;
  src: string;
  name: string;
}

export interface DigitalSampleImageGalleryProps {
  open: boolean;
  onClose: Dispatch<void>;
  selectedImage: number;
  onSelectedImageChange: Dispatch<number>;
  images: Array<DigitalSampleImage>;
}

export const DigitalSampleImageGallery: FC<DigitalSampleImageGalleryProps> = ({
  open,
  onClose,
  images,
  selectedImage,
  onSelectedImageChange,
}) => {
  const safeIndex = Math.min(
    Math.max(selectedImage, 0),
    Math.max(images.length - 1, 0),
  );

  const onPrevious = () =>
    safeIndex > 0 && onSelectedImageChange(safeIndex - 1);
  const onNext = () =>
    safeIndex + 1 < images.length && onSelectedImageChange(safeIndex + 1);

  const theme = useTheme();

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <FullScreenLoader open={open} zIndex={theme.zIndex.modal - 1} />
      <Lightbox
        style={{
          zIndex: theme.zIndex.modal,
        }}
        isOpen={open}
        onPrev={onPrevious}
        onNext={onNext}
        singleClickToZoom={true}
        renderHeader={() => (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            ml={3}
          >
            <Typography
              variant="h5"
              gutterBottom={false}
              color={theme.palette.common.white}
            ></Typography>
            <IconButton
              size="large"
              onClick={() => onClose()}
              sx={{ color: theme.palette.common.white }}
            >
              <CloseSharpIcon fontSize={"inherit"} />
            </IconButton>
          </Box>
        )}
        renderNextButton={() => (
          <NextButton
            totalImages={images.length}
            selectedImage={selectedImage}
            onClick={onNext}
          />
        )}
        renderPrevButton={() => (
          <PrevButton
            totalImages={images.length}
            selectedImage={selectedImage}
            onClick={onPrevious}
          />
        )}
        currentIndex={safeIndex}
        images={images.map((img) => ({
          src: img.src,
          alt: img.name,
          loading: "lazy",
        }))}
        onClose={onClose}
      />
    </>
  );
};

interface GalleryButtonProps {
  totalImages: number;
  selectedImage: number;
  onClick: Dispatch<any>;
}

const NextButton: FC<GalleryButtonProps> = ({
  totalImages,
  selectedImage,
  onClick,
}) => {
  const theme = useTheme();
  if (totalImages <= 1) return null;
  return (
    <IconButton
      sx={{ zIndex: 10, fontSize: "3rem", color: theme.palette.common.white }}
      disabled={totalImages - 1 <= selectedImage}
      onClick={onClick}
      size="large"
    >
      <ChevronRightSharpIcon fontSize={"inherit"} />
    </IconButton>
  );
};

const PrevButton: FC<GalleryButtonProps> = ({
  totalImages,
  selectedImage,
  onClick,
}) => {
  const theme = useTheme();
  if (totalImages <= 1) return null;
  return (
    <IconButton
      sx={{ zIndex: 10, fontSize: "3rem", color: theme.palette.common.white }}
      disabled={selectedImage <= 0}
      onClick={onClick}
      size="large"
    >
      <ChevronLeftSharpIcon fontSize={"inherit"} />
    </IconButton>
  );
};
